import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useIsDesktopViewport } from 'components/app-shell/useIsDesktopViewport'
import { AuthButton } from 'components/auth/AuthButton'
import { BrandLink } from 'components/ui/BrandLink'
import { Button } from 'components/ui/Button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/DropdownMenu'
import { IconButton } from 'components/ui/IconButton'
import { Sheet, SheetContent } from 'components/ui/Sheet'
import { useAuth } from 'contexts/AuthContext'
import { isAdminUserEmail } from 'utils/admin-user'
import { webLogger } from 'utils/logger'

interface HeaderProperties {
  variant?: 'full' | 'floating-menu'
  breadcrumb?: string
  onNewVideo?: () => void
  isMyVideosVisible?: boolean
  isNewVideoVisible?: boolean
  isSignOutVisible?: boolean
  isDisabled?: boolean
}

type MenuActionId = 'admin' | 'new' | 'sign-out' | 'videos'

interface MenuAction {
  id: MenuActionId
  isDisabled?: boolean
  label: string
}

const getMenuActions = ({
  isAdminMenuVisible,
  isDisabled,
  isMyVideosVisible,
  isSignOutVisible,
  user,
}: {
  isAdminMenuVisible: boolean
  isDisabled: boolean
  isMyVideosVisible: boolean
  isSignOutVisible: boolean
  user: unknown
}): MenuAction[] => {
  return [
    user ? { id: 'new', label: 'Analyze new video' } : undefined,
    user && isMyVideosVisible ? { id: 'videos', label: 'My Videos' } : undefined,
    user && isAdminMenuVisible ? { id: 'admin', label: 'Admin Settings' } : undefined,
    isSignOutVisible && user ? { id: 'sign-out', label: 'Sign Out', isDisabled } : undefined,
  ].filter((action): action is MenuAction => action !== undefined)
}

const renderMenuItems = (
  menuActions: MenuAction[],
  onSelect: (actionId: MenuActionId) => void,
  surface: 'dropdown' | 'sheet'
) => {
  return menuActions.map(action => {
    if (surface === 'dropdown') {
      return (
        <DropdownMenuItem
          disabled={action.isDisabled}
          key={action.id}
          onClick={() => {
            onSelect(action.id)
          }}
          value={action.id}
        >
          {action.label}
        </DropdownMenuItem>
      )
    }

    return (
      <Button
        className="header-sheet-action"
        disabled={action.isDisabled}
        key={action.id}
        onClick={() => {
          onSelect(action.id)
        }}
        size="lg"
        variant="menu"
      >
        {action.label}
      </Button>
    )
  })
}

const renderResponsiveMenu = ({
  isDesktop,
  isDisabled,
  isDropdownOpen,
  menuActions,
  onDropdownOpenChange,
  onMenuAction,
  onMobileMenuOpen,
  user,
}: {
  isDesktop: boolean
  isDisabled: boolean
  isDropdownOpen: boolean
  menuActions: MenuAction[]
  onDropdownOpenChange: (isOpen: boolean) => void
  onMenuAction: (actionId: MenuActionId) => void
  onMobileMenuOpen: () => void
  user: unknown
}) => {
  if (!user) {
    return null
  }

  if (isDesktop) {
    return (
      <DropdownMenu onOpenChange={onDropdownOpenChange} open={isDropdownOpen} positioning={{ placement: 'bottom-end' }}>
        <DropdownMenuTrigger asChild>
          <IconButton className="hamburger-menu" disabled={isDisabled} label="Open menu" variant="ghost">
            <span className="header-menu-glyph" aria-hidden>
              <span />
              <span />
              <span />
            </span>
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{renderMenuItems(menuActions, onMenuAction, 'dropdown')}</DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <IconButton
      className="hamburger-menu"
      disabled={isDisabled}
      label="Open menu"
      onClick={onMobileMenuOpen}
      variant="ghost"
    >
      <span className="header-menu-glyph" aria-hidden>
        <span />
        <span />
        <span />
      </span>
    </IconButton>
  )
}

export const Header = ({
  variant = 'full',
  breadcrumb,
  onNewVideo,
  isMyVideosVisible = true,
  isNewVideoVisible = false,
  isSignOutVisible = true,
  isDisabled = false,
}: HeaderProperties) => {
  /* --- Section 1: Hooks --- */
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const isDesktop = useIsDesktopViewport()

  /* --- Section 2: Local State --- */
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  /* --- Section 3: Derived Values --- */
  const isAdminMenuVisible = isAdminUserEmail(user?.email)
  const menuActions = getMenuActions({
    isAdminMenuVisible,
    isDisabled,
    isMyVideosVisible,
    isSignOutVisible,
    user,
  })

  /* --- Section 4: Event Handlers --- */
  const handleCloseAllMenus = () => {
    setIsMenuOpen(false)
    setIsDropdownOpen(false)
  }

  const handleMyVideosClick = () => {
    handleCloseAllMenus()
    navigate('/videos')
  }

  const handleSignOutClick = async () => {
    handleCloseAllMenus()
    try {
      await signOut()
    } catch (error) {
      webLogger.error('Sign out error', { error })
    }
  }

  const handleLogoClick = () => {
    navigate('/new')
  }

  const handleAnalyzeNewVideo = () => {
    handleCloseAllMenus()
    navigate('/new')
  }

  const handleAdminSettingsClick = () => {
    handleCloseAllMenus()
    navigate('/admin/settings')
  }

  const handleMenuAction = (actionId: MenuActionId) => {
    switch (actionId) {
      case 'new': {
        handleAnalyzeNewVideo()
        return
      }
      case 'videos': {
        handleMyVideosClick()
        return
      }
      case 'admin': {
        handleAdminSettingsClick()
        return
      }
      case 'sign-out': {
        void handleSignOutClick()
        return
      }
      default: {
        return
      }
    }
  }

  /* --- Section 5: Early Returns --- */
  if (variant === 'floating-menu') {
    return (
      <>
        <div className="landing-floating-menu">
          {renderResponsiveMenu({
            isDesktop,
            isDisabled,
            isDropdownOpen,
            menuActions,
            onDropdownOpenChange: setIsDropdownOpen,
            onMenuAction: handleMenuAction,
            onMobileMenuOpen: () => setIsMenuOpen(true),
            user,
          })}
        </div>

        {!isDesktop && (
          <Sheet onOpenChange={setIsMenuOpen} open={isMenuOpen}>
            <SheetContent>
              <div className="header-sheet-handle" />
              <div className="header-sheet-actions">{renderMenuItems(menuActions, handleMenuAction, 'sheet')}</div>
            </SheetContent>
          </Sheet>
        )}
      </>
    )
  }

  /* --- Section 6: Render --- */
  return (
    <>
      <div className="athlete-header">
        <div className="athlete-header-content">
          <div className="athlete-title-wrapper">
            <BrandLink className="athlete-title athlete-title-clickable" onClick={handleLogoClick}>
              <span className="athlete-title-text">
                Pocket Coach
                {breadcrumb ? <span className="breadcrumb-desktop"> › {breadcrumb}</span> : undefined}
              </span>
            </BrandLink>
          </div>
          <div className="athlete-header-actions">
            {renderResponsiveMenu({
              isDesktop,
              isDisabled,
              isDropdownOpen,
              menuActions,
              onDropdownOpenChange: setIsDropdownOpen,
              onMenuAction: handleMenuAction,
              onMobileMenuOpen: () => setIsMenuOpen(true),
              user,
            })}

            <div className="desktop-actions">
              {user && isMyVideosVisible ? (
                <Button
                  className="desktop-action-spacing"
                  disabled={isDisabled}
                  onClick={() => navigate('/videos')}
                  variant="primary"
                >
                  My Videos
                </Button>
              ) : undefined}
              {isNewVideoVisible ? (
                <Button disabled={isDisabled} onClick={onNewVideo} variant="primary">
                  New video
                </Button>
              ) : undefined}
              {isSignOutVisible && user ? <AuthButton isDisabled={isDisabled} /> : undefined}
            </div>
          </div>
        </div>
      </div>

      {breadcrumb ? (
        <div className="breadcrumb-mobile">
          <span className="breadcrumb-separator">›</span> {breadcrumb}
        </div>
      ) : undefined}

      {!isDesktop && (
        <Sheet onOpenChange={setIsMenuOpen} open={isMenuOpen}>
          <SheetContent>
            <div className="header-sheet-handle" />
            <div className="header-sheet-actions">{renderMenuItems(menuActions, handleMenuAction, 'sheet')}</div>
          </SheetContent>
        </Sheet>
      )}
    </>
  )
}
